import clsx from "clsx";
import { Auction, Patient } from "core/types";
import { dp } from "ds_legacy/materials/metrics";
import { useClientRect } from "dsl/hooks";
import { ArchivePatient } from "dsl/organisms/DeletePatientButton";
import { useEffect } from "react";
import type { CSSProperties } from "styled-components";
import AddSearchDropDown from "./AddSearchDropdown";
import { PatientAssignee } from "./PatientAssignee";
import { PatientIdentifier } from "./PatientIdentifier";
import { PatientTabs } from "./PatientTabs";
import {
  WatchedFieldContext,
  WatchedFieldContextType,
  useWatchedFields,
} from "./WatchedFieldContext";

export { WatchedFieldContext, useWatchedFields };
export type { WatchedFieldContextType };

export type PatientMenuProps = {
  auction: Auction;
  patient: Patient;
  storybookPosition?: CSSProperties["position"];
};

export function isValidPatient(
  patient: Patient | undefined,
  auction: Auction | undefined,
): boolean {
  return (
    auction?.solutions != null &&
    auction.solutions.length > 0 &&
    auction?.profile?.search_location?.latitude != null &&
    auction?.profile?.search_location?.longitude != null &&
    patient?.profile != null &&
    auction?.start_date != null &&
    (patient?.profile.age != null || patient?.profile.age_interval != null)
  );
}

export const handleSolutionsFromForm = (
  newAuction: Auction,
  auction: Auction,
) => {
  let solutions = newAuction?.solutions ?? auction?.solutions ?? null;
  if (typeof solutions === "number") {
    solutions = [solutions];
  }
  return solutions;
};

export default function PatientMenu({
  auction,
  patient,
  storybookPosition,
}: PatientMenuProps) {
  const [rect, ref] = useClientRect<HTMLDivElement>();

  useEffect(() => {
    if (rect?.height && storybookPosition !== "static") {
      document.documentElement.style.setProperty(
        "--patient-menu-height",
        dp(rect.height),
      );
    }
  }, [rect?.height, storybookPosition]);

  return (
    <>
      <div
        ref={ref}
        className={clsx(
          "top-navbar z-40 box-border h-patient-menu w-screen bg-white",
          {
            fixed: storybookPosition !== "static",
            static: storybookPosition === "static",
          },
        )}
      >
        <div
          className="flex w-full items-center justify-between overflow-x-auto px-2 pt-4 pb-3"
          data-testid="patient-menu-info-stripe"
        >
          <div className="flex items-center">
            <PatientIdentifier patient={patient} />
            <ArchivePatient patient={patient} />
          </div>
          <PatientAssignee auction={auction} patient={patient} />
        </div>
        <div className="flex h-search-type-tabs items-center justify-start border-b border-gray-300 px-2">
          <PatientTabs auctionId={auction.id} auctions={patient.auctions} />
          <AddSearchDropDown patient={patient} />
        </div>
      </div>
      {storybookPosition !== "static" && (
        <div className="h-patient-menu" data-testid="patient-menu-spacer" />
      )}
    </>
  );
}
